/**
 * Brokerage Hub — overview screen.
 *
 * A feature inside the Indirect Sales module: a registry of external
 * brokerage partner firms with at-a-glance performance. KPI strip, two
 * charts (top brokerages by converted leads, lead share), a filter bar,
 * the brokerage cards grid, and best / needs-attention summary cards.
 *
 * Route: /leads-dashboard/brokerage-hub
 */
import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Handshake, Plus, Search, Filter, RefreshCw, Trophy, AlertTriangle, Building2, Download, Upload,
  ArrowLeft, X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import HelpInfo from '../../components/HelpInfo';
import useBrokerages from './hooks/useBrokerages';
import { leaderboardCsvUrl, leaderboardXlsxUrl } from './api/brokerages';
import BrokerageKpiStrip from './components/BrokerageKpiStrip';
import BrokerageCard from './components/BrokerageCard';
import BrokerageModal from './components/BrokerageModal';
import BulkImportModal from './components/BulkImportModal';
import useIsBdm from './hooks/useIsBdm';
import InfoBadge from '@/components/InfoBadge';

// Doughnut slice palette — distinct, readable on light + dark.
const PIE_COLORS = [
  '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B',
  '#EF4444', '#06B6D4', '#EC4899', '#84CC16',
];

export default function BrokerageHub() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { token } = useAuth();
  const {
    summary, brokerages, loading, error, filters, updateFilter, resetFilters, refresh,
  } = useBrokerages();

  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // KPI tile drill-down — clicking a tile in BrokerageKpiStrip
  // filters / sorts the brokerage cards below. ``total_brokerages``
  // is the default ("show everything") view.
  const [kpiDrill, setKpiDrill] = useState('total_brokerages');
  // BDM users can't create or import brokerages — they only consume
  // the performance view. We hide the buttons in addition to the
  // backend's 403 so the UI doesn't dangle inert controls.
  const isBdm = useIsBdm();

  // Compute KPI tile values from the same per-brokerage stats the
  // drill operates on. The backend's ``summary.kpis.leads_mtd`` /
  // ``converted_mtd`` are month-to-date totals; pairing those with
  // a lifetime-scoped drill produced visibly inconsistent results
  // (e.g. tile said "Converted (MTD): 0" but drill correctly listed
  // 2 firms with lifetime wins). Computing both from the same
  // source guarantees they line up.
  const derivedKpis = useMemo(() => {
    const all = brokerages || [];
    const totalLeads = all.reduce((acc, b) => acc + (b?.stats?.total_leads || 0), 0);
    const totalConverted = all.reduce((acc, b) => acc + (b?.stats?.won_leads || 0), 0);
    const avg = totalLeads > 0
      ? Math.round((totalConverted / totalLeads) * 1000) / 10
      : 0;
    return {
      total_brokerages: all.length,
      leads_mtd: totalLeads,         // key kept for back-compat with the strip
      converted_mtd: totalConverted, // key kept for back-compat with the strip
      avg_conversion_rate: avg,
    };
  }, [brokerages]);

  // Apply the KPI drill-down on top of whatever filter/sort the
  // `useBrokerages` hook already applied. Kept here (not in the
  // hook) so the drill state stays local to this page and doesn't
  // leak into the API request shape — the hook's existing sort
  // option drives `?sort=` on the backend; the drill is a client-
  // side post-filter.
  const displayedBrokerages = useMemo(() => {
    if (!brokerages?.length) return brokerages || [];
    const withStats = (b) => b?.stats || {};
    let list = [...brokerages];
    if (kpiDrill === 'leads_mtd') {
      list = list
        .filter((b) => (withStats(b).total_leads || 0) > 0)
        .sort((a, b) => (withStats(b).total_leads || 0) - (withStats(a).total_leads || 0));
    } else if (kpiDrill === 'converted_mtd') {
      list = list
        .filter((b) => (withStats(b).won_leads || 0) > 0)
        .sort((a, b) => (withStats(b).won_leads || 0) - (withStats(a).won_leads || 0));
    } else if (kpiDrill === 'avg_conversion_rate') {
      list = list
        .filter((b) => (withStats(b).total_leads || 0) > 0)
        .sort((a, b) => (withStats(b).conversion_rate || 0) - (withStats(a).conversion_rate || 0));
    }
    return list;
  }, [brokerages, kpiDrill]);

  // Pipeline → Brokerage Hub deep-link. When the CEO clicks
  // "Open in Brokerage Hub" from the Indirect Sales Dashboard's
  // Pipeline strip, the active stage rides along as `?stage=<key>`.
  // We surface that as a banner up top so the user knows where the
  // navigation came from and can jump back to the Pipeline with a
  // single click. The filter mechanic on the brokerage list itself
  // (narrowing brokerages to those with leads in this stage)
  // requires a `stage` filter on the underlying API; the banner
  // makes the intent visible until that's wired up.
  const [searchParams, setSearchParams] = useSearchParams();
  const incomingStage = searchParams.get('stage');
  const stageHumanLabel = (k) => (k || '').replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const clearStageContext = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('stage');
    setSearchParams(next, { replace: true });
  };

  const openDetail = (id) => navigate(`/leads-dashboard/brokerage-hub/${id}`);
  const filtersActive =
    filters.search || filters.tier !== 'all' || filters.status !== 'all';

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`}>
      <div className="max-w-[1600px] mx-auto p-6 space-y-6">
        {/* Pipeline-context banner — only renders when the page is
            opened with a `?stage=<key>` deep-link from the Indirect
            Sales Dashboard's Pipeline. Click "Back to Pipeline" to
            return, or X to dismiss and stay in the Hub. */}
        {incomingStage && (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-violet-200 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-700"
               data-testid="brokerage-hub-pipeline-banner">
            <div className="w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-800/60 flex items-center justify-center text-violet-700 dark:text-violet-200 shrink-0">
              <Handshake size={15} />
            </div>
            <div className="text-sm min-w-0 flex-1">
              <span className="text-slate-500 dark:text-slate-400">From the Pipeline · stage</span>
              {' '}
              <span className="font-semibold text-slate-900 dark:text-white">
                {stageHumanLabel(incomingStage)}
              </span>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Showing every brokerage so you can spot which partners are contributing
                leads in this stage.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-violet-300 text-violet-700 hover:bg-violet-100"
              onClick={() => navigate(`/leads-dashboard?stage=${incomingStage}`)}
              title="Return to the Pipeline view with this stage selected"
            >
              <ArrowLeft size={13} className="mr-1" />
              Back to Pipeline
            </Button>
            <button
              type="button"
              onClick={clearStageContext}
              className="p-1 rounded hover:bg-violet-100 text-violet-700"
              title="Dismiss this banner"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
              <Handshake className="text-violet-500" size={28} />
              Brokerage Hub
              <HelpInfo
                align="inline"
                title="How to use the Brokerage Hub"
                steps={[
                  <>The four tiles up top track your brokerage partners at a glance — total firms, leads and conversions this month, and the average conversion rate.</>,
                  <>The two charts show your strongest partners (<em>top by converted leads</em>) and how referred leads are split across brokerages.</>,
                  <>Use the search, <strong>tier</strong> filter and sort to find a firm. Each card shows its tier, contact and lead stats — click a card to open its full detail.</>,
                  <>A <strong>RERA-expiry warning</strong> appears on any card whose licence lapses within 60 days, so renewals don't slip.</>,
                  <><strong>Add brokerage</strong> registers a new partner firm; the two cards at the bottom highlight your best performer and the one that needs attention.</>,
                ]}
                tip="Sort by Conversion rate to see which partners actually close — referral volume alone can be misleading."
              />
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              External brokerage partner firms — leads referred, conversion and performance.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(leaderboardXlsxUrl(token), '_blank')}
              disabled={!brokerages.length}
              title="Download leaderboard as Excel (.xlsx) — formatted with BDM/Sales Manager columns"
            >
              <Download size={14} className="mr-1.5" />
              Leaderboard Excel
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(leaderboardCsvUrl(token), '_blank')}
              disabled={!brokerages.length}
              title="Download as plain CSV"
            >
              CSV
            </Button>
            {!isBdm && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportOpen(true)}
                title="Bulk import brokerages from CSV/Excel — duplicates auto-detected"
              >
                <Upload size={14} className="mr-1.5" />
                Import
              </Button>
            )}
            {!isBdm && (
              <Button size="sm" onClick={() => setModalOpen(true)}>
                <Plus size={14} className="mr-1.5" />
                Add brokerage
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {/* KPI strip — each tile is a drill-down tab. Clicking
            re-selects the active key, which filters / sorts the
            brokerage cards below via ``displayedBrokerages``. */}
        <BrokerageKpiStrip
          kpis={derivedKpis}
          loading={loading && !summary}
          activeKey={kpiDrill}
          onSelect={(key) => setKpiDrill((curr) => (curr === key ? 'total_brokerages' : key))}
        />
        {/* Drill-status chip — sits between the strip and the cards
            so the user knows the list is scoped + has a one-click
            escape hatch. Hidden when the default "total" view is
            active. */}
        {kpiDrill !== 'total_brokerages' && (
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
              Showing{' '}
              {kpiDrill === 'leads_mtd' && 'firms with leads — sorted by lead count'}
              {kpiDrill === 'converted_mtd' && 'firms with wins — sorted by conversions'}
              {kpiDrill === 'avg_conversion_rate' && 'firms with leads — sorted by conversion rate'}
              {' '}({displayedBrokerages.length} of {brokerages.length})
            </span>
            <button
              type="button"
              className="rounded px-2 py-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              onClick={() => setKpiDrill('total_brokerages')}
            >
              Clear ✕
            </button>
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Top Brokerages by Converted Leads</CardTitle>
              <CardDescription>Best six performers this period.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading && !summary ? (
                <Skeleton className="h-[260px] w-full" />
              ) : summary?.top_converted?.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={summary.top_converted}
                    layout="vertical"
                    margin={{ left: 20, right: 16, top: 4, bottom: 4 }}
                  >
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={120}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip />
                    <Bar dataKey="converted" name="Converted" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ChartEmpty text="No converted leads yet." />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Lead Share by Brokerage</CardTitle>
              <CardDescription>How referred leads are distributed.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading && !summary ? (
                <Skeleton className="h-[260px] w-full" />
              ) : summary?.lead_share?.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={summary.lead_share}
                      dataKey="total"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={2}
                    >
                      {summary.lead_share.map((entry, i) => (
                        <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <ChartEmpty text="No leads referred yet." />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Filter bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={16} className="text-slate-400" />
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="Search brokerage name…"
                  className="pl-9"
                />
              </div>
              <Select value={filters.tier} onValueChange={(v) => updateFilter('tier', v)}>
                <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Tier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tiers</SelectItem>
                  <SelectItem value="Top">Top</SelectItem>
                  <SelectItem value="Mid">Mid</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.sort} onValueChange={(v) => updateFilter('sort', v)}>
                <SelectTrigger className="h-9 w-[180px]"><SelectValue placeholder="Sort by" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="converted">Sort: Converted leads</SelectItem>
                  <SelectItem value="total">Sort: Total leads</SelectItem>
                  <SelectItem value="conversion">Sort: Conversion rate</SelectItem>
                  <SelectItem value="name">Sort: Name</SelectItem>
                </SelectContent>
              </Select>
              {filtersActive && (
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={resetFilters}>
                  Clear
                </Button>
              )}
              <span className="ml-auto text-xs text-slate-500">
                {brokerages.length} brokerage{brokerages.length === 1 ? '' : 's'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Cards grid */}
        {loading && !brokerages.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-[180px] w-full rounded-xl" />
            ))}
          </div>
        ) : displayedBrokerages.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {displayedBrokerages.map((b) => (
              <BrokerageCard key={b.id} brokerage={b} onClick={() => openDetail(b.id)} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-14 text-center">
              <Building2 size={36} className="mx-auto text-slate-300 mb-3" />
              <p className="font-medium text-slate-700 dark:text-slate-200">
                {filtersActive ? 'No brokerages match these filters' : 'No brokerages yet'}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {filtersActive
                  ? 'Try clearing the search or tier filter.'
                  : 'Add your first external brokerage partner to start tracking referrals.'}
              </p>
              {!filtersActive && (
                <Button size="sm" className="mt-4" onClick={() => setModalOpen(true)}>
                  <Plus size={14} className="mr-1.5" />
                  Add brokerage
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Best / Needs attention — top-3 of each. We read the new
            list fields when present and fall back to the legacy
            single-entry fields so this works even if the backend
            hasn't been redeployed yet. */}
        {summary && (
          (summary.best_performers?.length || summary.needs_attention_list?.length)
          || summary.best_performer || summary.needs_attention
        ) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SummaryColumn
              tone="emerald"
              icon={Trophy}
              title="Best performing"
              info="Top 3 brokerages by conversion rate (with won-leads as tie-breaker so 3-of-10 ranks above 1-of-3). Only firms with at least one lead are considered."
              brokerages={
                summary.best_performers?.length
                  ? summary.best_performers
                  : (summary.best_performer ? [summary.best_performer] : [])
              }
              emptyLabel="Not enough data yet."
            />
            <SummaryColumn
              tone="amber"
              icon={AlertTriangle}
              title="Needs attention"
              info="Bottom 3 brokerages by conversion rate. Firms already shown in Best Performing are excluded so the same name never appears in both lists."
              brokerages={
                summary.needs_attention_list?.length
                  ? summary.needs_attention_list
                  : (summary.needs_attention ? [summary.needs_attention] : [])
              }
              emptyLabel="No underperforming brokerages."
            />
          </div>
        )}
      </div>

      <BrokerageModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSaved={refresh}
      />
      <BulkImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={refresh}
      />
    </div>
  );
}

const ChartEmpty = ({ text }) => (
  <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">
    {text}
  </div>
);

const SUMMARY_TONES = {
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
};

// One column for the Best / Needs-attention pair. Renders up to 3
// brokerages stacked, each as a compact row. Replaces the original
// SummaryCard (single firm) — the legacy export name is kept below as
// a thin wrapper so anything still importing it doesn't break.
const SummaryColumn = ({ tone, icon: Icon, title, info, brokerages = [], emptyLabel }) => {
  const headerColor = tone === 'emerald' ? 'text-emerald-700' : 'text-amber-700';
  // Rank-pill background — keeps the visual hierarchy "1 > 2 > 3"
  // without leaning on font weight (which is already in use).
  const rankBg = tone === 'emerald'
    ? ['bg-emerald-600', 'bg-emerald-500', 'bg-emerald-400']
    : ['bg-amber-600', 'bg-amber-500', 'bg-amber-400'];
  return (
    <Card className={`border ${SUMMARY_TONES[tone]?.split(' ')[0] || ''}`}>
      <CardContent className="p-4">
        <div className={`flex items-center gap-2 text-sm font-medium ${headerColor}`}>
          <Icon size={16} />
          {title}
          {brokerages.length > 0 && (
            <span className="ml-1 rounded-full bg-white/60 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
              Top {brokerages.length}
            </span>
          )}
          <InfoBadge text={info} />
        </div>
        {brokerages.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">{emptyLabel}</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {brokerages.slice(0, 3).map((b, i) => (
              <li
                key={b.name + i}
                className="flex items-center gap-3 rounded-md border border-slate-200/60 bg-white/70 px-2.5 py-2 dark:border-slate-700/40 dark:bg-slate-900/40"
              >
                <span
                  className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${rankBg[i] || rankBg[2]}`}
                  title={`Rank ${i + 1}`}
                >
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {b.name}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                    <span>{b.total_leads} leads</span>
                    <span>{b.won_leads} won</span>
                  </div>
                </div>
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  {b.conversion_rate}%
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

// Back-compat shim — anything still importing the old single-entry
// SummaryCard keeps working by forwarding to the new column with a
// one-element list. New code should call SummaryColumn directly.
const SummaryCard = ({ tone, icon, title, brokerage }) => (
  <SummaryColumn
    tone={tone}
    icon={icon}
    title={title}
    brokerages={brokerage ? [brokerage] : []}
    emptyLabel="Not enough data yet."
  />
);
